import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/Card'
import { Wand2 } from 'lucide-react'
import appIcon from '@/assets/images/icon-128.png'
import { GoogleIcon, MicrosoftIcon } from '@/components/icons'
import { AuthBackground } from '@/components/auth/AuthBackground'

interface Invitation {
  token: string
  email: string
  role: string
  account_name: string
}

interface RegisterProps {
  errors?: Record<string, string>
  invitation?: Invitation | null
  prefilled_email?: string | null
}

export default function Register({ errors, invitation, prefilled_email }: RegisterProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: prefilled_email || '',
    password: '',
    password_confirmation: '',
  })

  const submitProvider = (provider: 'google_oauth2' | 'microsoft_graph') => {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `/auth/${provider}`

    if (csrfToken) {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrfToken
      form.appendChild(tokenInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    router.post('/register', { user: formData }, {
      onFinish: () => setIsSubmitting(false),
    })
  }

  return (
    <>
      <Head title="Register" />

      <AuthBackground>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <img src={appIcon} alt="Banshee" className="size-16 mx-auto mb-2 rounded-xl" />
            <CardTitle>
              {invitation ? `Join ${invitation.account_name}` : 'Create your account'}
            </CardTitle>
            <CardDescription>
              {invitation
                ? `Create an account to join as ${invitation.role}`
                : 'Start chatting with your customers'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First name</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                  {errors?.first_name && (
                    <p className="text-sm text-destructive">{errors.first_name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last name</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                  {errors?.last_name && (
                    <p className="text-sm text-destructive">{errors.last_name}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ghost@example.com"
                  required
                  readOnly={!!invitation}
                  className={invitation ? 'bg-muted cursor-not-allowed' : ''}
                />
                {invitation && (
                  <p className="text-xs text-muted-foreground">
                    Email is locked to the invitation address
                  </p>
                )}
                {errors?.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
                {errors?.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
                <p className="text-xs text-muted-foreground">Minimum 12 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirmation">Confirm password</Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  value={formData.password_confirmation}
                  onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                  required
                />
                {errors?.password_confirmation && (
                  <p className="text-sm text-destructive">{errors.password_confirmation}</p>
                )}
              </div>

              <Button type="submit" className="w-full cursor-pointer" disabled={isSubmitting}>
                {isSubmitting ? 'Creating account...' : 'Create account'}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            {/* Alternative Sign-up Options */}
            <div className="space-y-3">
              <Button
                variant="outline"
                type="button"
                className="w-full cursor-pointer"
                onClick={() => submitProvider('google_oauth2')}
              >
                <GoogleIcon className="size-5" />
                Continue with Google
              </Button>
              <Button
                variant="outline"
                type="button"
                className="w-full cursor-pointer"
                onClick={() => submitProvider('microsoft_graph')}
              >
                <MicrosoftIcon className="size-5" />
                Continue with Microsoft
              </Button>
              <Button
                variant="outline"
                className="w-full cursor-pointer"
                onClick={() => window.location.href = '/login/magic'}
              >
                <Wand2 className="size-4" />
                Sign up with magic link
              </Button>
            </div>
          </CardContent>

          <CardFooter className="justify-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <a href="/login" className="text-primary hover:underline cursor-pointer">Sign in</a>
            </p>
          </CardFooter>
        </Card>
      </AuthBackground>
    </>
  )
}
